/**
 * The licence register's wire layer — four reads, no writes, and three absences.
 *
 * ===========================================================================
 * TRANSCRIBED FROM THE SERVER'S OWN CONTRACT, NOT GUESSED
 * ===========================================================================
 * Every type below is copied field-for-field from
 * `apps/api/src/modules/tally-licences/dto/tally-licence.dto.ts`. Where that file
 * declares a `readonly` array it stays readonly here, because a screen that
 * mutated a payload would be editing the only copy of a fact the server measured.
 *
 * ===========================================================================
 * ⚠ THREE THINGS ARE ABSENT, AND EACH ABSENCE IS THE DESIGN
 * ===========================================================================
 * · **No activation key, in any type, under any name.** The register stores none
 *   — the importer refuses a run that could persist one — so the field is absent
 *   from the payload rather than null, and it is absent here rather than typed as
 *   `string | null`. A nullable field on a client type is an invitation to render
 *   an empty cell labelled "Key", which is the one cell on this screen that must
 *   never exist. `tally-licences.test.tsx` sweeps every rendered screen for the
 *   word rather than trusting this paragraph.
 *
 * · **No money field of any name.** There is no money in this estate: the
 *   `$126`/`$378` strings in the workbook are PRODUCT names the owner has not
 *   declassified. So nothing here touches `formatMoney` or `isMaskedAmount`, and
 *   the masking discipline the quote and inquiry screens keep is not engaged —
 *   not because it was forgotten, but because there is nothing to mask. If a
 *   "value" column is ever added to this register, this comment is where the
 *   reader learns the screens were written on the assumption it could not be.
 *
 * · **No `customer_id` on the licence inside a serial's chain.** The server
 *   deliberately omits it (a serial resolves to a HISTORY: 230 of them carry more
 *   than one canonical customer, 231 on the observed names the design measured), and
 *   the only holder fact on the wire is
 *   `customers[].is_latest`, which cannot be rendered without its context.
 *
 * ===========================================================================
 * `disclosures` IS PROSE ON THE PAYLOAD, AND THE SCREENS RENDER IT VERBATIM
 * ===========================================================================
 * Nothing branches on a disclosure string. The client neither parses, filters nor
 * re-orders them: a screen that dropped one would be deciding which caveat the
 * operator does not need, and that decision belongs to whoever wrote the number.
 */

import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';

/* ─────────────────────────────────────────────────────────── vocabularies ── */

/** `tally_licences.status`. `retired` exists in the schema; nothing sets it yet. */
export type LicenceStatus = 'active' | 'lapsed' | 'retired';

/** Where `cover_to` came from. The importer never writes one, so it is `none`. */
export type CoverToSource = 'none' | 'operator' | 'crf';

/** How the owner classified a canonical customer. `undecided` is a real state. */
export type LicenceClassification = 'customer' | 'reseller' | 'group_entity' | 'undecided';

/** `import` for a workbook row, `crf` for a renewal sold as a real document. */
export type LicenceEventSource = 'import' | 'crf';

export const LICENCE_SORTS = ['serial', 'last_event', 'first_event', 'customer', 'events'] as const;
export type LicenceSort = (typeof LICENCE_SORTS)[number];

export const DUE_SOON_SORTS = ['due', 'serial', 'customer', 'last_event'] as const;
export type DueSoonSort = (typeof DUE_SOON_SORTS)[number];

/** How the worklist treats rows billed to the group's own arm. `exclude` is the default. */
export type InternalMode = 'exclude' | 'include' | 'only';

/** Suppressed rows are hidden by default. They are never deleted. */
export type SuppressedMode = 'hide' | 'show' | 'only';

/* ───────────────────────────────────────────────────────────── the register ── */

export interface TallyLicenceRow {
  readonly id: string;
  readonly country_id: number;
  readonly serial: string;
  readonly status: LicenceStatus;
  readonly first_event_on: string;
  readonly last_event_on: string;
  readonly cover_to: string | null;
  readonly cover_to_source: CoverToSource;
  readonly latest_customer_id: string | null;
  readonly latest_customer_name: string | null;
  readonly latest_customer_classification: LicenceClassification | null;
  /** DISTINCT canonical customers this serial has carried. `> 1` = it changed hands. */
  readonly customer_count: number;
  readonly event_count: number;
  readonly disputed_event_count: number;
  readonly latest_product_id: string | null;
  readonly latest_product_name: string | null;
  readonly latest_product_code: string | null;
}

export interface TallyLicencePage {
  readonly data: TallyLicenceRow[];
  readonly page: number;
  readonly perPage: number;
  /** The SERVER's count over the whole filtered set. Never `data.length`. */
  readonly total: number;
  /** The COUNTRY's calendar day the server used. The screen must not re-derive it. */
  readonly today: string;
  readonly sort: LicenceSort;
  readonly dir: 'asc' | 'desc';
  readonly disclosures: readonly string[];
}

/* ─────────────────────────────────────────────── a serial, and its chain ── */

export interface TallyLicenceChainLink {
  readonly customer_id: string | null;
  readonly canonical_name: string | null;
  readonly classification: LicenceClassification | null;
  /** NULL for every row until owner decision D-2 lands. NULL is the truthful value. */
  readonly party_id: string | null;
  readonly dispute_codes: readonly string[];
  readonly first_event_on: string;
  readonly last_event_on: string;
  readonly event_count: number;
  /** LATEST BY EVENT DATE. Not "the owner" — see this file's header. */
  readonly is_latest: boolean;
}

export interface TallyLicenceEventRow {
  readonly id: string;
  readonly event_on: string;
  readonly qty: number;
  readonly event_source: LicenceEventSource;
  readonly customer_id: string | null;
  readonly customer_name: string | null;
  readonly product_id: string;
  readonly product_name: string | null;
  readonly product_code: string | null;
  readonly so_line_id: string | null;
  readonly batch_id: string | null;
  readonly dispute_codes: readonly string[];
  /** The RAW cells, verbatim: normalisation is a claim, the raw string is evidence. */
  readonly source_sheet: string | null;
  readonly source_row: number | null;
  readonly source_date_text: string | null;
  readonly source_product_text: string | null;
  readonly source_party_text: string | null;
  readonly remarks: string | null;
}

export interface TallyLicenceChain {
  readonly serial: string;
  readonly licence: {
    readonly id: string;
    readonly country_id: number;
    readonly serial: string;
    readonly status: LicenceStatus;
    readonly first_event_on: string;
    readonly last_event_on: string;
    readonly cover_to: string | null;
    readonly cover_to_source: CoverToSource;
    readonly party_id: string | null;
    readonly notes: string | null;
  };
  readonly customers: readonly TallyLicenceChainLink[];
  readonly customer_count: number;
  readonly changed_hands: boolean;
  readonly events: readonly TallyLicenceEventRow[];
  readonly event_count: number;
  readonly today: string;
  readonly disclosures: readonly string[];
}

/* ──────────────────────────────────────── the register's own customers ── */

export interface TallyLicenceCustomerRow {
  readonly id: string;
  readonly country_id: number;
  readonly canonical_name: string;
  readonly classification: LicenceClassification;
  /**
   * The owner's partner flag — migration 36, transcribed from the server DTO
   * (`tally-licence.dto.ts`). A BOOLEAN AND NOTHING MORE: no discount rate and no
   * pricing tier travel with it, because the live product already has a per-quote
   * discount field the admin sets and it varies partner to partner. Orthogonal to
   * `classification` — a partner may be a `reseller`, a `customer` or `undecided`,
   * and all three occur. No HTTP write exists for it anywhere: the importer's apply
   * step derives it from the approved map and the owner's named list
   * (`scripts/tally-licences/partners.ts`), so the screens READ it and nothing more.
   */
  readonly is_partner: boolean;
  /** NULL until D-2. This register creates no `parties` row. */
  readonly party_id: string | null;
  readonly dispute_codes: readonly string[];
  readonly licence_count: number;
  readonly event_count: number;
  readonly first_event_on: string | null;
  readonly last_event_on: string | null;
}

/**
 * The facts that change how the list READS — computed live by the server, so the
 * screen reports THIS database rather than a figure that was true when a design
 * document was written.
 */
export interface TallyLicenceCustomerSummary {
  readonly customers: number;
  readonly events: number;
  readonly group_entity_customers: number;
  /** Rows billed to the group's own arm: their real end customer is recorded nowhere. */
  readonly group_entity_events: number;
  readonly single_event_customers: number;
  readonly disputed_customers: number;
}

export interface TallyLicenceCustomerPage {
  readonly data: TallyLicenceCustomerRow[];
  readonly page: number;
  readonly perPage: number;
  readonly total: number;
  readonly summary: TallyLicenceCustomerSummary;
  readonly sort: 'name' | 'licences' | 'events' | 'last_event';
  readonly dir: 'asc' | 'desc';
  readonly disclosures: readonly string[];
}

/* ────────────────────────────────────────────── the due-soon worklist ── */

export interface DueSoonRow {
  readonly licence_id: string;
  readonly serial: string;
  readonly last_event_on: string;
  /** Operator truth when present; the estimate is beside it, never instead of it. */
  readonly cover_to: string | null;
  readonly estimated_cover_to: string;
  readonly due_basis: 'operator' | 'estimated';
  readonly due_on: string;
  readonly days_remaining: number;
  readonly customer_id: string | null;
  readonly customer_name: string | null;
  readonly classification: LicenceClassification | null;
  readonly party_id: string | null;
  readonly product_id: string | null;
  readonly product_name: string | null;
  readonly product_code: string | null;
  /** Flagged and hidden by default — never removed. The match is `(party, product)`. */
  readonly suppressed_by_live_contract: boolean;
}

export interface DueSoonPage {
  readonly data: DueSoonRow[];
  readonly page: number;
  readonly perPage: number;
  /** What this filter SHOWS. `matched` is the count before hiding. */
  readonly total: number;
  readonly matched: number;
  /** How many of `matched` carry the flag — reported even when they are hidden. */
  readonly suppressed_count: number;
  readonly group_entity_count: number;
  readonly today: string;
  readonly window_days: number;
  readonly include_lapsed: boolean;
  readonly internal: InternalMode;
  readonly suppressed: SuppressedMode;
  /** `'last_event_on + 365 days'` — stated so nothing infers it from the numbers. */
  readonly estimate_basis: string;
  readonly sort: DueSoonSort;
  readonly dir: 'asc' | 'desc';
  readonly disclosures: readonly string[];
}

/* ──────────────────────────────────────────────────────────────── reads ── */

export interface LicenceRegisterFilters {
  /** A serial PREFIX. The server answers 400 for a non-numeric term, naming why. */
  serial?: string;
  customer?: string;
  product?: string;
  classification?: string;
  status?: string;
  dispute?: string;
  disputed?: boolean;
  changed_hands?: boolean;
  sort?: string;
  dir?: string;
  page?: number;
  perPage?: number;
}

/**
 * Filters are PASSED THROUGH UNVALIDATED, the same rule the testing register keeps.
 *
 * The server owns every vocabulary here and answers 400 naming the accepted tokens.
 * Sanitising a parameter in the client would mean a URL saying `?status=bogus`
 * quietly rendered the WHOLE register while claiming to be filtered — and on a
 * register whose reader is auditing an estate, a silently unfiltered list is the
 * one wrong answer that looks right.
 */
export async function fetchLicenceRegister(
  countryCode: string,
  filters: LicenceRegisterFilters = {},
  signal?: AbortSignal,
): Promise<TallyLicencePage> {
  return apiRequest<TallyLicencePage>(`/countries/${countryCode}/tally-licences`, {
    query: {
      serial: filters.serial,
      customer: filters.customer,
      product: filters.product,
      classification: filters.classification,
      status: filters.status,
      dispute: filters.dispute,
      disputed: filters.disputed,
      changed_hands: filters.changed_hands,
      sort: filters.sort,
      dir: filters.dir,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

/**
 * One serial's chain.
 *
 * The serial is a Tally-issued STRING, unique only per country — not a row id —
 * so it is encoded rather than interpolated raw. The server's route binds no
 * entity for exactly this reason, and its own `country_id` predicate is what makes
 * the lookup country-local.
 */
export async function fetchLicenceBySerial(
  countryCode: string,
  serial: string,
  signal?: AbortSignal,
): Promise<TallyLicenceChain> {
  return apiRequest<TallyLicenceChain>(
    `/countries/${countryCode}/tally-licences/by-serial/${encodeURIComponent(serial)}`,
    { signal },
  );
}

export interface LicenceCustomerFilters {
  /** A contains-match on the canonical name. A NAME match, and never an identity. */
  q?: string;
  classification?: string;
  dispute?: string;
  sort?: string;
  dir?: string;
  page?: number;
  perPage?: number;
}

export async function fetchLicenceCustomers(
  countryCode: string,
  filters: LicenceCustomerFilters = {},
  signal?: AbortSignal,
): Promise<TallyLicenceCustomerPage> {
  return apiRequest<TallyLicenceCustomerPage>(
    `/countries/${countryCode}/tally-licences/customers`,
    {
      query: {
        q: filters.q,
        classification: filters.classification,
        dispute: filters.dispute,
        sort: filters.sort,
        dir: filters.dir,
        page: filters.page,
        perPage: filters.perPage,
      },
      signal,
    },
  );
}

export interface DueSoonFilters {
  window?: number;
  include_lapsed?: boolean;
  internal?: InternalMode;
  suppressed?: SuppressedMode;
  sort?: string;
  dir?: string;
  page?: number;
  perPage?: number;
}

export async function fetchLicencesDueSoon(
  countryCode: string,
  filters: DueSoonFilters = {},
  signal?: AbortSignal,
): Promise<DueSoonPage> {
  return apiRequest<DueSoonPage>(`/countries/${countryCode}/tally-licences/due-soon`, {
    query: {
      window: filters.window,
      include_lapsed: filters.include_lapsed,
      internal: filters.internal,
      suppressed: filters.suppressed,
      sort: filters.sort,
      dir: filters.dir,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

/* ──────────────────────────────────────── the dispute review report ── */

/**
 * A report's own arithmetic, published so the screen can CHECK it rather than
 * believe it.
 *
 * `holds` is `sum === total`, computed on the server by the only function that
 * constructs one of these. The screen renders the disagreement when it is false —
 * a screen that showed the numbers and swallowed the mismatch would be the exact
 * failure the reconciliation exists to catch.
 */
export interface ReportReconciliation {
  readonly claim: string;
  readonly parts: readonly { readonly label: string; readonly count: number }[];
  readonly total: number;
  readonly sum: number;
  readonly holds: boolean;
}

/** A handful of rows under a code, as EVIDENCE. The raw workbook cells, verbatim. */
export interface DisputeSampleRow {
  readonly serial: string;
  readonly event_on: string;
  readonly customer_name: string | null;
  readonly product_name: string | null;
  readonly source_sheet: string | null;
  readonly source_row: number | null;
  readonly source_date_text: string | null;
  readonly source_party_text: string | null;
}

export interface DisputeCodeRow {
  readonly code: string;
  readonly scope: 'row';
  /** The importer's own note. Rendered verbatim; the screen never paraphrases it. */
  readonly note: string;
  readonly events: number;
  readonly licences: number;
  readonly customers: number;
  readonly first_event_on: string | null;
  readonly last_event_on: string | null;
  /** The API query that opens exactly this set. The screen builds a UI link from it. */
  readonly filter: string;
  readonly sample: readonly DisputeSampleRow[];
}

/**
 * ⚠ A BATCH-LEVEL CODE HAS NO COUNT FIELD AT ALL — not a nullable one.
 *
 * D-9 and D-10 cover 2,665 and 857 rows of the owner's file, but the register
 * stores those decisions as the MAP it imported under rather than as a code on a
 * row, so there is no row set to count. `0` would read as "this affects nothing",
 * which is the opposite of true, so the number is absent and the screen prints
 * "No row scope" in its place.
 */
export interface BatchDisputeCodeRow {
  readonly code: string;
  readonly scope: 'batch';
  readonly note: string;
}

/** A report this register refuses to produce, with the reason and what would change it. */
export interface RefusedReport {
  readonly id: string;
  readonly title: string;
  readonly reason: string;
  /** `null` when nothing known would make it possible — no source row names a user. */
  readonly returns_when: string | null;
}

export interface LicenceDisputeReport {
  readonly report: 'disputes';
  readonly country_id: number;
  readonly today: string;
  readonly codes: readonly DisputeCodeRow[];
  readonly batch_codes: readonly BatchDisputeCodeRow[];
  /** Rows carrying AT LEAST ONE code. The headline. */
  readonly events_flagged: number;
  readonly events_unflagged: number;
  readonly events_total: number;
  /** The sum of the per-code counts — LARGER, because a row can carry two codes. */
  readonly code_row_sum: number;
  readonly multi_code_events: number;
  readonly excess_codes: number;
  readonly customers_flagged: number;
  readonly customers_total: number;
  readonly refused_reports: readonly RefusedReport[];
  readonly reconciliations: readonly ReportReconciliation[];
  readonly disclosures: readonly string[];
}

/**
 * The owner's review surface, in one read.
 *
 * The other five reports are API surfaces with no screen in this cluster — the one
 * the flagged rows exist for is this one, and it carries the refused-report list
 * with it so the reader meets the refusals where they are already looking.
 */
export async function fetchLicenceDisputeReport(
  countryCode: string,
  signal?: AbortSignal,
): Promise<LicenceDisputeReport> {
  return apiRequest<LicenceDisputeReport>(
    `/countries/${countryCode}/tally-licences/reports/disputes`,
    { signal },
  );
}

/* ─────────────────────────────────────────────────────────────── errors ── */

/**
 * Register failures as sentences an operator can act on.
 *
 * Re-authored per feature rather than imported from a sibling — the house pattern
 * the quotes, work and CRF features each keep, for the reason they each record: a
 * shared helper grows a branch per feature until nobody can tell which sentence
 * any surface produces.
 *
 * 400 is PASSED THROUGH. Every refusal this API raises names the vocabulary it
 * refused against ("sort must be one of: …", "serial must be digits — it is a
 * Tally serial, not a search term."), and a house sentence would replace the only
 * text that tells the reader what to type instead.
 *
 * A 403 never names the permission key — the house rule on every feature.
 */
export function licenceErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') {
      return 'That licence belongs to another country.';
    }
    if (error.status === 404) return 'No licence in this country carries that serial.';
    if (error.status === 403) {
      return 'This account is not allowed to read the licence register in this country.';
    }
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}

/** True when the failure is the register simply not holding that serial. */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 404;
}

/* ─────────────────────────────────────────────────────────── query keys ── */

export const licenceKeys = {
  register: (cc: string, filters: LicenceRegisterFilters) =>
    ['tally-licences', cc, 'register', filters] as const,
  serial: (cc: string, serial: string) => ['tally-licences', cc, 'serial', serial] as const,
  customers: (cc: string, filters: LicenceCustomerFilters) =>
    ['tally-licences', cc, 'customers', filters] as const,
  dueSoon: (cc: string, filters: DueSoonFilters) =>
    ['tally-licences', cc, 'due-soon', filters] as const,
  disputes: (cc: string) => ['tally-licences', cc, 'reports', 'disputes'] as const,
};
